import { apiHosts } from "@/api/constants/apiHosts";
import { ToastOptionsSuccess, type ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";

import type { AxiosResponse } from "axios";
import type {
  DustMonitorClass,
  DustMonitorClassNew,
  DustMonitorClassQuery,
  DustMonitorInviteCandidate,
} from "./dustMonitorClass";
import dustMonitorClassService from "./dustMonitorClassService";

const URL = "dust-monitor-classes";
const ENTITY_NAME = "Dust Monitor Class";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<DustMonitorClass>(apiHosts.AQMS, axiosParams);

const getDustMonitorClasses = (params: DustMonitorClassQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getDustMonitorClassById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

function getDustMonitorClassDisplayDate(dmc: DustMonitorClass) {
  return dmc.classLocalDaysDisplay?.join(" & ") ?? null;
}

const inviteDustMonitorAttendees = (
  classId: number,
  electronicFormIds: number[],
  config: PagingApiAndNotifierConfig = {},
) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = "Invitations";
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = "Invitations";
    config?.errorToastCb?.(options);
  };
  return api.post(URL + "/" + classId + "/invite-attendees", { electronicFormIds }, { successToastCb, errorToastCb });
};

const getDustMonitorInviteCandidates = (classId: number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = "Invite Candidates";
    config?.errorToastCb?.(options);
  };
  return api.getPaged<Paging<DustMonitorInviteCandidate>>(`${URL}/${classId}/invite-candidates`, {
    successToastCb,
    errorToastCb,
  });
};

const getNewDustMonitorClass = async () => {
  return {
    data: dustMonitorClassService.getInitialData(),
  } as AxiosResponse<DustMonitorClassNew, any>;
};

const createDustMonitorClass = (dustMonitorClass: DustMonitorClass, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.post(URL, dustMonitorClass, { successToastCb, errorToastCb });
};

const updateDustMonitorClass = (dustMonitorClass: DustMonitorClass, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.put(`${URL}/${dustMonitorClass.id}`, dustMonitorClass, { successToastCb, errorToastCb });
};

const saveDustMonitorClass = (dustMonitorClass: DustMonitorClass, config: PagingApiAndNotifierConfig = {}) => {
  if (dustMonitorClass.id) {
    return updateDustMonitorClass(dustMonitorClass, config);
  }
  return createDustMonitorClass(dustMonitorClass, config);
};

export type IssueDustMonitorCardsResponse = {
  issued: number;
  revoked: number;
  dustMonitorClass: DustMonitorClass;
};

const issueCardsApi = apiService<IssueDustMonitorCardsResponse>(apiHosts.AQMS, axiosParams);

const issueDustMonitorCards = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = "Dust Monitor Cards";
    config?.errorToastCb?.(options);
  };
  return issueCardsApi.post(`${URL}/${id}/issue-cards`, {}, { successToastCb, errorToastCb });
};

const deleteDustMonitorClassById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.delete(`${URL}/${id}`, { successToastCb, errorToastCb });
};

export {
  createDustMonitorClass,
  deleteDustMonitorClassById,
  getDustMonitorClassById,
  getDustMonitorClassDisplayDate,
  getDustMonitorClasses,
  getDustMonitorInviteCandidates,
  getNewDustMonitorClass,
  inviteDustMonitorAttendees,
  issueDustMonitorCards,
  saveDustMonitorClass,
  updateDustMonitorClass,
};
